// Small promise helpers with no dependencies.

/**
 * Run fn over items with at most `limit` concurrent invocations, preserving
 * input order in the results. A rejection does not stop the remaining items
 * (Promise.allSettled semantics) — inspect the settled results to report
 * partial failures.
 */
export async function mapWithConcurrency<T, R>(
  limit: number,
  items: readonly T[],
  fn: (item: T, index: number) => Promise<R>,
): Promise<PromiseSettledResult<R>[]> {
  const results = new Array<PromiseSettledResult<R>>(items.length)
  let next = 0
  const worker = async () => {
    while (true) {
      const i = next++
      if (i >= items.length) return
      try {
        results[i] = { status: 'fulfilled', value: await fn(items[i], i) }
      } catch (error) {
        results[i] = { status: 'rejected', reason: error }
      }
    }
  }
  const workers = Array.from(
    { length: Math.max(1, Math.min(limit, items.length)) },
    () => worker(),
  )
  await Promise.all(workers)
  return results
}
